import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
  Delete as DeleteIcon,
  FilterListOff,
  Print,
  Refresh,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TableFilterCell } from '../components/TableFilterCell';
import { statusLabel } from '../i18n/translate';
import { appUserApi, employeeApi } from '../services/api';
import { AppUserCreateRequest, AppUserRecord, AppUserStatus, AppUserUpdateRequest } from '../types/appUser';
import { Employee } from '../types/employee';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

type UserFormState = AppUserCreateRequest;

const DEFAULT_FORM: UserFormState = {
  firstName: '',
  lastName: '',
  username: '',
  password: '',
  phoneNumber: '',
  role: 'USER',
  status: 'ACTIVE',
  linkedEmployeeId: null,
  canManageFleet: false,
  canManageReservations: false,
  canManageEmployees: false,
  canViewReports: false,
  canManageFinances: false,
};

const PAGE_SIZE = 8;

function getStatusColor(status: AppUserStatus) {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'default';
    case 'SUSPENDED':
      return 'warning';
    case 'BANNED':
      return 'error';
    default:
      return 'default';
  }
}

interface UserManagementPageProps {
  includeSuperusers?: boolean;
}

export const UserManagementPage: React.FC<UserManagementPageProps> = ({ includeSuperusers = false }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState<AppUserRecord | null>(null);
  const [formData, setFormData] = useState<UserFormState>(DEFAULT_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    role: '',
    status: '',
    linkedEmployee: '',
  });

  const { data: users = [], isLoading } = useQuery(
    ['appUsers', includeSuperusers],
    () => appUserApi.getAll(includeSuperusers)
  );
  const { data: availableEmployees = [] } = useQuery(
    ['availableEmployees', selectedUser?.id ?? 'new'],
    () => employeeApi.getAvailableForAppUser(selectedUser?.id),
    {
      enabled: isCreating || !!selectedUser,
    }
  );

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setSelectedUser(null);
    setIsCreating(false);
    setIsDirty(false);
  };

  const invalidateUserQueries = () => {
    queryClient.invalidateQueries('appUsers');
    queryClient.invalidateQueries('employees');
    queryClient.invalidateQueries('availableEmployees');
  };

  const createMutation = useMutation(appUserApi.create, {
    onSuccess: () => {
      invalidateUserQueries();
      toast.success(t('users.messages.created', 'Application user created successfully'));
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('users.messages.createFailed', 'Failed to create application user'));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: AppUserUpdateRequest }) => appUserApi.update(id, data),
    {
      onSuccess: (user) => {
        invalidateUserQueries();
        toast.success(t('users.messages.updated', 'Application user updated successfully'));
        setSelectedUser(user);
        setIsDirty(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('users.messages.updateFailed', 'Failed to update application user'));
      },
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }: { id: string; status: AppUserStatus }) => appUserApi.updateStatus(id, status),
    {
      onSuccess: () => {
        invalidateUserQueries();
        toast.success(t('users.messages.statusUpdated', 'Application user status updated successfully'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('users.messages.statusFailed', 'Failed to update application user status'));
      },
    }
  );

  const deleteMutation = useMutation(appUserApi.delete, {
    onSuccess: () => {
      invalidateUserQueries();
      toast.success(t('users.messages.deleted', 'Application user deleted successfully'));
      setShowDeleteDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('users.messages.deleteFailed', 'Failed to delete application user'));
    },
  });

  const filteredUsers = useMemo(() => {
    return users.filter((user) => (
      (!filters.firstName || user.firstName.toLowerCase().includes(filters.firstName.toLowerCase())) &&
      (!filters.lastName || user.lastName.toLowerCase().includes(filters.lastName.toLowerCase())) &&
      (!filters.username || user.username.toLowerCase().includes(filters.username.toLowerCase())) &&
      (!filters.phoneNumber || user.phoneNumber.toLowerCase().includes(filters.phoneNumber.toLowerCase())) &&
      (!filters.role || user.role.toLowerCase().includes(filters.role.toLowerCase())) &&
      (!filters.status || user.status.toLowerCase().includes(filters.status.toLowerCase())) &&
      (!filters.linkedEmployee || (user.linkedEmployeeName || '').toLowerCase().includes(filters.linkedEmployee.toLowerCase()))
    ));
  }, [filters, users]);

  const paginatedUsers = filteredUsers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));

  const handleStartCreate = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        resetForm();
        setIsCreating(true);
      });
      setShowUnsavedDialog(true);
      return;
    }
    resetForm();
    setIsCreating(true);
  };

  const handleSelectUser = (user: AppUserRecord) => {
    if (isDirty) {
      setPendingAction(() => () => handleSelectUserInternal(user));
      setShowUnsavedDialog(true);
      return;
    }
    handleSelectUserInternal(user);
  };

  const handleSelectUserInternal = (user: AppUserRecord) => {
    setSelectedUser(user);
    setIsCreating(false);
    setIsDirty(false);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      password: '',
      phoneNumber: user.phoneNumber,
      role: user.role,
      status: user.status,
      linkedEmployeeId: user.linkedEmployeeId ?? null,
      canManageFleet: user.canManageFleet,
      canManageReservations: user.canManageReservations,
      canManageEmployees: user.canManageEmployees,
      canViewReports: user.canViewReports,
      canManageFinances: user.canManageFinances,
    });
  };

  const handleEmployeeSelection = (employeeId: number | null) => {
    const linkedEmployee = availableEmployees.find((employee) => employee.id === employeeId);
    setFormData((prev) => ({
      ...prev,
      linkedEmployeeId: employeeId,
      firstName: !selectedUser && !prev.firstName && linkedEmployee ? linkedEmployee.firstName : prev.firstName,
      lastName: !selectedUser && !prev.lastName && linkedEmployee ? linkedEmployee.lastName : prev.lastName,
      phoneNumber: !selectedUser && !prev.phoneNumber && linkedEmployee?.phoneNumber ? linkedEmployee.phoneNumber : prev.phoneNumber,
    }));
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.username.trim() || !formData.phoneNumber.trim()) {
      toast.error(t('validation.requiredUserFields', 'Please fill in the required user fields.'));
      return;
    }

    if (isCreating && !formData.password.trim()) {
      toast.error(t('users.passwordRequired', 'Password is required for new users'));
      return;
    }

    const payload: AppUserCreateRequest = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      username: formData.username.trim(),
      password: formData.password,
      phoneNumber: formData.phoneNumber.trim(),
      role: formData.role,
      status: formData.status,
      linkedEmployeeId: formData.linkedEmployeeId ?? null,
      canManageFleet: !!formData.canManageFleet,
      canManageReservations: !!formData.canManageReservations,
      canManageEmployees: !!formData.canManageEmployees,
      canViewReports: !!formData.canViewReports,
      canManageFinances: !!formData.canManageFinances,
    };

    if (isCreating) {
      createMutation.mutate(payload);
      return;
    }

    if (!selectedUser) {
      return;
    }

    updateMutation.mutate({
      id: selectedUser.id.toString(),
      data: {
        ...payload,
        password: formData.password.trim() ? formData.password : undefined,
      },
    });
  };

  const handleDiscardChanges = () => {
    setIsDirty(false);
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const linkedEmployeeLabel = (employee: Employee) => {
    const code = employee.employeeCode ? ` (${employee.employeeCode})` : '';
    const title = employee.jobTitle ? ` - ${employee.jobTitle}` : '';
    return `${employee.fullName}${code}${title}`;
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleStartCreate} size="small" sx={{ textTransform: 'none' }}>
                {t('common.new')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                size="small"
                disabled={!isCreating && !selectedUser}
                sx={{ textTransform: 'none' }}
              >
                {t('common.save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteDialog(true)}
                size="small"
                disabled={!selectedUser}
                sx={{ textTransform: 'none' }}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<Refresh fontSize="small" />} size="small" onClick={invalidateUserQueries} sx={{ textTransform: 'none' }}>
                    {t('common.refresh')}
                  </Button>
                  <Button variant="outlined" startIcon={<Print fontSize="small" />} size="small" sx={{ textTransform: 'none' }}>
                    {t('common.print')}
                  </Button>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {filteredUsers.length === 0 ? '0' : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filteredUsers.length)} {t('common.of')} {filteredUsers.length}
                  </Typography>
                  <IconButton size="small" onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))} disabled={page >= totalPages - 1}>
                    <ArrowForward fontSize="small" />
                  </IconButton>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<FilterListOff fontSize="small" />}
                    onClick={() => {
                      setFilters({
                        firstName: '',
                        lastName: '',
                        username: '',
                        phoneNumber: '',
                        role: '',
                        status: '',
                        linkedEmployee: '',
                      });
                      setPage(0);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.clearFilters')}
                  </Button>
                </Box>
              </Toolbar>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableFilterCell placeholder={t('fields.firstName')} value={filters.firstName} onChange={(value) => setFilters((prev) => ({ ...prev, firstName: value }))} />
                      <TableFilterCell placeholder={t('fields.lastName')} value={filters.lastName} onChange={(value) => setFilters((prev) => ({ ...prev, lastName: value }))} />
                      <TableFilterCell placeholder={t('fields.username', 'Username')} value={filters.username} onChange={(value) => setFilters((prev) => ({ ...prev, username: value }))} />
                      <TableFilterCell placeholder={t('fields.phoneNumber')} value={filters.phoneNumber} onChange={(value) => setFilters((prev) => ({ ...prev, phoneNumber: value }))} />
                      <TableFilterCell placeholder={t('fields.linkedEmployee', 'Employee')} value={filters.linkedEmployee} onChange={(value) => setFilters((prev) => ({ ...prev, linkedEmployee: value }))} />
                      <TableFilterCell placeholder={t('fields.role')} value={filters.role} onChange={(value) => setFilters((prev) => ({ ...prev, role: value }))} />
                      <TableFilterCell placeholder={t('fields.status')} value={filters.status} onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          {t('management.emptyUsers', 'No application users found')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedUsers.map((user) => (
                        <TableRow key={user.id} hover selected={selectedUser?.id === user.id} onClick={() => handleSelectUser(user)} sx={{ cursor: 'pointer' }}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.phoneNumber}</TableCell>
                          <TableCell>{user.linkedEmployeeName || '-'}</TableCell>
                          <TableCell>
                            {user.role === 'SUPERUSER'
                              ? t('roles.superuser')
                              : user.role === 'ADMIN'
                              ? t('roles.admin')
                              : t('roles.user')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabel(t, user.status)}
                              size="small"
                              color={getStatusColor(user.status) as any}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateStatusMutation.mutate({
                                  id: user.id.toString(),
                                  status: user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                                });
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedUser) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 2, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">
                    {isCreating ? t('users.newUser', 'New User Account') : selectedUser?.fullName || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim()}
                  </Typography>
                  {!isCreating && selectedUser && (
                    <Chip label={statusLabel(t, selectedUser.status)} size="small" color={getStatusColor(selectedUser.status) as any} />
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.firstName')}
                          value={formData.firstName}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, firstName: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.lastName')}
                          value={formData.lastName}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, lastName: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.username', 'Username')}
                          value={formData.username}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, username: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={isCreating ? t('fields.password') : t('users.passwordKeepCurrent', 'Password (leave blank to keep current)')}
                          type="password"
                          value={formData.password}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, password: event.target.value }));
                            setIsDirty(true);
                          }}
                          required={isCreating}
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.phoneNumber')}
                          value={formData.phoneNumber}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, phoneNumber: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('fields.role')}</InputLabel>
                          <Select
                            value={formData.role}
                            label={t('fields.role')}
                            onChange={(event) => {
                              setFormData((prev) => ({ ...prev, role: event.target.value as AppUserRecord['role'] }));
                              setIsDirty(true);
                            }}
                          >
                            <MenuItem value="USER">{t('roles.user')}</MenuItem>
                            <MenuItem value="ADMIN">{t('roles.admin')}</MenuItem>
                            {includeSuperusers && (
                              <MenuItem value="SUPERUSER">{t('roles.superuser')}</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('fields.linkedEmployee', 'Linked Employee')}</InputLabel>
                          <Select
                            value={formData.linkedEmployeeId ?? ''}
                            label={t('fields.linkedEmployee', 'Linked Employee')}
                            onChange={(event) => handleEmployeeSelection(event.target.value ? Number(event.target.value) : null)}
                          >
                            <MenuItem value="">{t('common.none')}</MenuItem>
                            {availableEmployees.map((employee) => (
                              <MenuItem key={employee.id} value={employee.id}>
                                {linkedEmployeeLabel(employee)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('fields.status')}</InputLabel>
                          <Select
                            value={formData.status}
                            label={t('fields.status')}
                            onChange={(event) => {
                              setFormData((prev) => ({ ...prev, status: event.target.value as AppUserStatus }));
                              setIsDirty(true);
                            }}
                          >
                            <MenuItem value="ACTIVE">{t('status.active')}</MenuItem>
                            <MenuItem value="INACTIVE">{t('status.inactive')}</MenuItem>
                            <MenuItem value="SUSPENDED">{t('status.suspended')}</MenuItem>
                            <MenuItem value="BANNED">{t('status.banned')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('users.permissionsTitle', 'Permissions')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                          control={<Checkbox checked={!!formData.canManageFleet} onChange={(event) => {
                            setFormData((prev) => ({ ...prev, canManageFleet: event.target.checked }));
                            setIsDirty(true);
                          }} />}
                          label={t('fields.manageFleet')}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={!!formData.canManageReservations} onChange={(event) => {
                            setFormData((prev) => ({ ...prev, canManageReservations: event.target.checked }));
                            setIsDirty(true);
                          }} />}
                          label={t('fields.manageReservations')}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={!!formData.canManageEmployees} onChange={(event) => {
                            setFormData((prev) => ({ ...prev, canManageEmployees: event.target.checked }));
                            setIsDirty(true);
                          }} />}
                          label={t('fields.manageEmployees')}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={!!formData.canViewReports} onChange={(event) => {
                            setFormData((prev) => ({ ...prev, canViewReports: event.target.checked }));
                            setIsDirty(true);
                          }} />}
                          label={t('fields.viewReports')}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={!!formData.canManageFinances} onChange={(event) => {
                            setFormData((prev) => ({ ...prev, canManageFinances: event.target.checked }));
                            setIsDirty(true);
                          }} />}
                          label={t('fields.manageFinances')}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>{t('management.deleteUser', 'Delete User')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('dialogs.deleteBody', {
              item: selectedUser?.fullName || t('fields.user', 'user'),
              defaultValue: `Are you sure you want to delete ${selectedUser?.fullName || 'this user'}? This action cannot be undone.`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedUser && deleteMutation.mutate(selectedUser.id.toString())}
            disabled={deleteMutation.isLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)}>
        <DialogTitle>{t('dialogs.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('dialogs.unsavedBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)}>{t('common.keepEditing')}</Button>
          <Button color="error" variant="contained" onClick={handleDiscardChanges}>
            {t('common.discardChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
